import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { ConfirmDialog } from '../../ui/Modal';

export const KIND_LABEL: Record<string, string> = { person: 'Personen', organisation: 'Organisationen' };

/** Technischen Schlüssel aus einem Namen ableiten: Kleinbuchstaben, Ziffern, Unterstrich, beginnt mit Buchstaben */
export function slugKey(s: string, max = 40, fallback = 'feld'): string {
  const k = s
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[0-9]/, 'f$&')
    .slice(0, max)
    .replace(/_+$/, '');
  return k || fallback;
}

/** Schlüssel eindeutig machen: bei Kollision _2, _3 … */
export function uniqueKey(base: string, taken: Iterable<string>): string {
  const set = new Set(taken);
  if (!set.has(base)) return base;
  let i = 2;
  while (set.has(`${base}_${i}`)) i++;
  return `${base}_${i}`;
}

/** Element in einer Liste um eine Position verschieben */
export function moveItem<T>(list: T[], from: number, to: number): T[] {
  const out = [...list];
  const [x] = out.splice(from, 1);
  out.splice(to, 0, x!);
  return out;
}

/* ---------- Ungespeicherte Änderungen ---------- */
type LeaveCtx = { setDirty: (id: string, dirty: boolean) => void; guard: (fn: () => void) => void };
const Ctx = createContext<LeaveCtx | null>(null);

/** Hält fest, welche Verwaltungsseite ungespeicherte Änderungen hat, und fragt vor dem Verwerfen nach. */
export function UnsavedProvider({ children }: { children: ReactNode }) {
  const dirty = useRef(new Set<string>());
  const [pending, setPending] = useState<(() => void) | null>(null);
  const setDirty = useCallback((id: string, d: boolean) => {
    if (d) dirty.current.add(id);
    else dirty.current.delete(id);
  }, []);
  const guard = useCallback((fn: () => void) => {
    if (dirty.current.size) setPending(() => fn);
    else fn();
  }, []);
  const value = useMemo(() => ({ setDirty, guard }), [setDirty, guard]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {pending && (
        <ConfirmDialog
          title="Änderungen verwerfen?"
          text="Du hast Änderungen, die noch nicht gespeichert sind."
          confirmLabel="Verwerfen"
          danger
          onClose={() => setPending(null)}
          onConfirm={() => {
            dirty.current.clear();
            const fn = pending;
            setPending(null);
            fn();
          }}
        />
      )}
    </Ctx.Provider>
  );
}

/** Meldet ungespeicherte Änderungen an (Rückfrage beim Wechsel und beim Schließen des Tabs). Gibt guard(fn) zurück. */
export function useUnsaved(dirty: boolean): (fn: () => void) => void {
  const ctx = useContext(Ctx);
  const id = useId();
  useEffect(() => {
    ctx?.setDirty(id, dirty);
    return () => ctx?.setDirty(id, false);
  }, [ctx, id, dirty]);
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);
  return ctx?.guard ?? ((fn) => fn());
}

/** Rückfrage-Funktion ohne eigenen Zustand (z. B. für Navigationsleisten) */
export function useLeaveGuard(): (fn: () => void) => void {
  return useContext(Ctx)?.guard ?? ((fn) => fn());
}
